import { useState, type ReactNode } from 'react';
import { Icon } from './Icon';

export interface RailSearchProps<T> {
  placeholder: string;
  query: string;
  setQuery: (q: string) => void;
  focused: boolean; // controlled so the modal can gate fetches on focus
  setFocused: (f: boolean) => void;
  items: T[]; // dropdown candidates (already recents-or-matches)
  idOf: (t: T) => string;
  isCurrent: (t: T) => boolean;
  renderRow: (t: T, q: string) => ReactNode; // inner of the .orow button
  onPick: (t: T) => void;
  restLabel: string; // group label when there's no query ("Recent" / "Helpers")
  addLabel: string; // "Add new owner" / "Add new helper"
  onAdd: () => void;
  current?: ReactNode; // the current-entity card (curowner), shown under the input
  noMatchText: string; // "No owner matches" / "No helper matches"
  // Open the dropdown on empty focus too (default true). Set FALSE when a
  // persistent RailRecents list already fills the rail — otherwise focusing the
  // box shows a redundant second recents list on top of it.
  showOnFocus?: boolean;
}

// The rail head shared by every master-detail modal: search input + add button,
// the current-entity card, and the results dropdown with keyboard nav. Source of
// truth = the owner↔booking modal's railSearch, promoted to a global.
export function RailSearch<T>({
  placeholder, query, setQuery, focused, setFocused, items, idOf, isCurrent,
  renderRow, onPick, restLabel, addLabel, onAdd, current, noMatchText, showOnFocus = true,
}: RailSearchProps<T>) {
  const [ai, setAi] = useState(0);
  // Typing always opens the results; an empty-focus open is gated by showOnFocus.
  const show = query.trim().length > 0 || (focused && showOnFocus);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAi((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAi((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { const t = items[ai]; if (t) onPick(t); }
    else if (e.key === 'Escape') { if (query) setQuery(''); else setFocused(false); }
  };

  return (
    <div className="railhead">
      <div className="sf">
        <svg className="ic" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="4.5" /><path d="M10.5 10.5l4 4" /></svg>
        <input placeholder={placeholder} value={query} autoComplete="off"
          onChange={(e) => { setQuery(e.target.value); setAi(0); }} onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)} onKeyDown={onKey} />
        <button type="button" className="sf-add" title={addLabel} onMouseDown={onAdd}><Icon name="plus" size={14} /></button>
      </div>
      {current}
      {show && (
        <div className="results">
          {items.length > 0 ? (
            <>
              <div className="grouplbl"><span className="eyebrow">{query.trim() ? 'Matches' : restLabel}</span><span className="eyebrow">{items.length}</span></div>
              {items.map((t, i) => (
                <button key={idOf(t)} type="button" className={'orow' + (isCurrent(t) ? ' cur' : '') + (i === ai ? ' active' : '')} onMouseEnter={() => setAi(i)} onMouseDown={() => onPick(t)}>
                  {renderRow(t, query)}
                </button>
              ))}
              <button type="button" className="res-add" onMouseDown={onAdd}><span className="res-plus">+</span>{addLabel}</button>
            </>
          ) : (
            <div className="nomatch">
              <div className="t">{noMatchText}{query.trim() ? ` “${query.trim()}”` : ''}</div>
              <button type="button" className="res-add" onMouseDown={onAdd}><span className="res-plus">+</span>{addLabel}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
